import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Copy, Trash2, KeyRound, Webhook } from "lucide-react";

function fmt(dt) {
  if (!dt) return "—";
  return new Date(dt).toLocaleString([], {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

export default function Integrations() {
  const [keys, setKeys] = useState([]);
  const [hooks, setHooks] = useState([]);
  const [label, setLabel] = useState("");
  const [hookUrl, setHookUrl] = useState("");
  const [newKey, setNewKey] = useState(null); // { key, prefix, label }
  const [newHook, setNewHook] = useState(null); // { secret, url }

  const load = async () => {
    try {
      const [k, w] = await Promise.all([
        api.get("/api-keys"),
        api.get("/webhooks"),
      ]);
      setKeys(k.data);
      setHooks(w.data);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  useEffect(() => {
    load();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps -- initial fetch on mount

  const createKey = async (e) => {
    e.preventDefault();
    if (!label.trim()) return;
    try {
      const { data } = await api.post("/api-keys", { label });
      setNewKey(data);
      setLabel("");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const revokeKey = async (id) => {
    try {
      await api.delete(`/api-keys/${id}`);
      toast.success("Key revoked");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const createHook = async (e) => {
    e.preventDefault();
    if (!hookUrl.trim()) return;
    try {
      const { data } = await api.post("/webhooks", { url: hookUrl });
      setNewHook(data);
      setHookUrl("");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const deleteHook = async (id) => {
    try {
      await api.delete(`/webhooks/${id}`);
      toast.success("Webhook deleted");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const copy = async (v) => {
    try {
      await navigator.clipboard.writeText(v);
      toast.success("Copied to clipboard");
    } catch {
      // Fallback for HTTP / iframe contexts where clipboard API is denied
      toast.message("Copy manually", { description: v });
    }
  };

  // Use runtime origin so the API-docs example shows whichever domain the
  // admin is currently viewing (production URL in production, preview in dev).
  const backendUrl =
    typeof window !== "undefined" ? window.location.origin : "";

  return (
    <div className="mx-auto max-w-5xl p-4 md:p-8">
      <div className="border border-border">
        <div className="border-b border-border p-6 md:p-8">
          <p className="mono text-[10px] uppercase tracking-[0.3em] text-[#C9A85F]">
            → integrations
          </p>
          <h1 className="mt-2 text-4xl leading-tight tracking-tight md:text-5xl">
            API keys &amp; webhooks
          </h1>
          <p className="mt-2 text-sm text-muted-foreground max-w-xl">
            Push openings from your POS system (Square, Toast, Mindbody, or any
            custom integration) and get real-time notifications when requests
            arrive.
          </p>
        </div>

        {/* API Keys */}
        <section className="border-b border-border">
          <header className="flex items-center gap-2 border-b border-border p-4 md:px-6">
            <KeyRound size={14} className="text-[#C9A85F]" />
            <h2 className="text-xl tracking-tight">API keys</h2>
          </header>

          <form onSubmit={createKey} className="grid grid-cols-1 gap-3 border-b border-border p-4 md:grid-cols-[1fr_auto] md:px-6">
            <div className="grid gap-1.5">
              <Label className="mono text-[10px] uppercase tracking-[0.2em]" htmlFor="key-label">
                Label
              </Label>
              <Input
                id="key-label"
                data-testid="new-api-key-label"
                value={label}
                onChange={(e) => setLabel(e.target.value)}
                placeholder="e.g. Toast production"
                className="rounded-none"
              />
            </div>
            <div className="flex items-end">
              <button
                type="submit"
                data-testid="new-api-key-btn"
                className="bg-foreground text-background px-4 py-2 text-xs uppercase tracking-widest hover:bg-[#C9A85F] focus-visible:ring-2 focus-visible:ring-[#C9A85F] focus-visible:ring-offset-2 transition-colors"
              >
                Generate key
              </button>
            </div>
          </form>

          {keys.length === 0 ? (
            <div className="p-6 mono text-xs text-muted-foreground md:px-6">
              No API keys yet. Generate one to start pushing openings from your
              POS.
            </div>
          ) : (
            <ul>
              {keys.map((k) => (
                <li
                  key={k.id}
                  data-testid={`api-key-row-${k.id}`}
                  className="grid grid-cols-1 gap-3 border-b border-border p-4 md:grid-cols-[1fr_auto_auto_auto] md:items-center md:gap-6 md:px-6"
                >
                  <div>
                    <p className="text-sm">{k.label}</p>
                    <p className="mono text-[10px] uppercase tracking-widest text-muted-foreground">
                      {k.prefix}••••••••
                    </p>
                  </div>
                  <span className="mono text-[10px] uppercase tracking-widest text-muted-foreground">
                    created {fmt(k.created_at)}
                  </span>
                  <span className="mono text-[10px] uppercase tracking-widest text-muted-foreground">
                    last used {fmt(k.last_used_at)}
                  </span>
                  <button
                    data-testid={`revoke-key-${k.id}`}
                    onClick={() => revokeKey(k.id)}
                    aria-label={`Revoke key ${k.label}`}
                    className="inline-flex items-center gap-1.5 border border-border px-3 py-1.5 text-[11px] uppercase tracking-widest hover:border-[#F87171] hover:text-[#F87171] focus-visible:ring-2 focus-visible:ring-[#F87171] transition-colors"
                  >
                    <Trash2 size={12} /> Revoke
                  </button>
                </li>
              ))}
            </ul>
          )}
        </section>

        {/* Webhooks */}
        <section>
          <header className="flex items-center gap-2 border-b border-border p-4 md:px-6">
            <Webhook size={14} className="text-[#C9A85F]" />
            <h2 className="text-xl tracking-tight">Webhooks</h2>
          </header>

          <form onSubmit={createHook} className="grid grid-cols-1 gap-3 border-b border-border p-4 md:grid-cols-[1fr_auto] md:px-6">
            <div className="grid gap-1.5">
              <Label className="mono text-[10px] uppercase tracking-[0.2em]" htmlFor="hook-url">
                Endpoint URL
              </Label>
              <Input
                id="hook-url"
                data-testid="new-webhook-url"
                type="url"
                value={hookUrl}
                onChange={(e) => setHookUrl(e.target.value)}
                placeholder="https://your-pos.example.com/bookaway/hook"
                className="rounded-none"
              />
              <p className="mono text-[10px] uppercase tracking-widest text-muted-foreground">
                Receives request.created and opening.filled events, HMAC-SHA256
                signed via X-Spotfiller-Signature.
              </p>
            </div>
            <div className="flex items-end">
              <button
                type="submit"
                data-testid="new-webhook-btn"
                className="bg-foreground text-background px-4 py-2 text-xs uppercase tracking-widest hover:bg-[#C9A85F] focus-visible:ring-2 focus-visible:ring-[#C9A85F] focus-visible:ring-offset-2 transition-colors"
              >
                Register
              </button>
            </div>
          </form>

          {hooks.length === 0 ? (
            <div className="p-6 mono text-xs text-muted-foreground md:px-6">
              No webhooks registered.
            </div>
          ) : (
            <ul>
              {hooks.map((h) => (
                <li
                  key={h.id}
                  data-testid={`webhook-row-${h.id}`}
                  className="grid grid-cols-1 gap-3 border-b border-border p-4 md:grid-cols-[1fr_auto_auto] md:items-center md:gap-6 md:px-6"
                >
                  <div>
                    <p className="text-sm break-all">{h.url}</p>
                    <p className="mono text-[10px] uppercase tracking-widest text-muted-foreground">
                      {h.events.join(" · ")}
                    </p>
                  </div>
                  <button
                    onClick={() => copy(h.secret)}
                    aria-label="Copy webhook secret"
                    className="inline-flex items-center gap-1.5 border border-border px-3 py-1.5 text-[11px] uppercase tracking-widest hover:border-foreground focus-visible:ring-2 focus-visible:ring-[#C9A85F] transition-colors"
                  >
                    <Copy size={12} /> Copy secret
                  </button>
                  <button
                    data-testid={`delete-webhook-${h.id}`}
                    onClick={() => deleteHook(h.id)}
                    aria-label={`Delete webhook ${h.url}`}
                    className="inline-flex items-center gap-1.5 border border-border px-3 py-1.5 text-[11px] uppercase tracking-widest hover:border-[#F87171] hover:text-[#F87171] focus-visible:ring-2 focus-visible:ring-[#F87171] transition-colors"
                  >
                    <Trash2 size={12} /> Delete
                  </button>
                </li>
              ))}
            </ul>
          )}
        </section>

        {/* Docs */}
        <section className="border-t border-border p-6 md:p-8">
          <p className="mono text-[10px] uppercase tracking-[0.3em] text-[#C9A85F]">
            → quickstart
          </p>
          <h3 className="mt-1 text-lg tracking-tight">
            Post an opening from your POS
          </h3>
          <pre className="mono mt-3 overflow-x-auto border border-border bg-muted/40 p-4 text-[11px] leading-relaxed">
{`curl -X POST ${backendUrl}/api/public/v1/openings \\
  -H "X-API-Key: sk_..." \\
  -H "Content-Type: application/json" \\
  -d '{
    "external_id": "reservation-9271",
    "service_type": "Restaurant",
    "title": "Cancellation — table for 2",
    "address": "1234 Fillmore St, San Francisco",
    "lat": 37.7614, "lng": -122.4353,
    "start_time": "2026-02-14T19:00:00Z",
    "end_time":   "2026-02-14T21:00:00Z",
    "price": null
  }'`}
          </pre>
          <p className="mt-3 text-xs text-muted-foreground">
            Adapters for Square, Toast, and Mindbody are provider-side shims
            over this endpoint — each maps their reservation/cancellation event
            to a POST/DELETE here. Contact us for the reference implementations.
          </p>
        </section>
      </div>

      {/* Show-once key dialog */}
      <Dialog open={!!newKey} onOpenChange={(o) => !o && setNewKey(null)}>
        <DialogContent className="rounded-none max-w-md">
          <DialogHeader>
            <DialogTitle>Copy your API key now</DialogTitle>
            <DialogDescription>
              This is the only time it will be shown. Store it somewhere safe.
            </DialogDescription>
          </DialogHeader>
          <div
            data-testid="new-key-value"
            className="mono break-all border border-[#C9A85F] bg-muted/40 p-3 text-xs"
          >
            {newKey?.key}
          </div>
          <button
            onClick={() => {
              copy(newKey.key);
              setNewKey(null);
            }}
            className="w-full gold-metallic-bg px-4 py-2.5 text-xs font-semibold uppercase tracking-widest text-black focus-visible:ring-2 focus-visible:ring-foreground transition-colors"
          >
            Copy &amp; close
          </button>
        </DialogContent>
      </Dialog>

      {/* Show-once webhook secret dialog */}
      <Dialog open={!!newHook} onOpenChange={(o) => !o && setNewHook(null)}>
        <DialogContent className="rounded-none max-w-md">
          <DialogHeader>
            <DialogTitle>Webhook registered</DialogTitle>
            <DialogDescription>
              Verify incoming requests using this secret with the
              X-Spotfiller-Signature header.
            </DialogDescription>
          </DialogHeader>
          <div className="mono break-all border border-[#C9A85F] bg-muted/40 p-3 text-xs">
            {newHook?.secret}
          </div>
          <button
            onClick={() => {
              copy(newHook.secret);
              setNewHook(null);
            }}
            className="w-full gold-metallic-bg px-4 py-2.5 text-xs font-semibold uppercase tracking-widest text-black focus-visible:ring-2 focus-visible:ring-foreground transition-colors"
          >
            Copy &amp; close
          </button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
